import { Spinner } from '@ui';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';
import { offline } from '../../lib/api';
import { useMe, type Me, type Permission } from './api';

interface SessionContextValue {
  /** The signed-in user including their role in the business. */
  me: Me;
  /**
   * Whether the user's role grants `permission` (USR-009). Only for showing
   * or hiding UI; the server checks every request.
   */
  can: (permission: Permission) => boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * Guards the signed-in part of the app: redirects to sign-in without a
 * session and provides the user.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const me = useMe();
  const value = useMemo(() => {
    if (!me.data) return null;
    const granted = new Set(me.data.permissions);
    const can = (permission: Permission) => granted.has(permission);
    return { me: me.data, can };
  }, [me.data]);
  // Captured entries and cached answers belong to this user (ADR 0027).
  const userId = me.data?.id;
  useEffect(() => {
    if (userId) void offline.setOwner(userId);
  }, [userId]);

  if (me.isPending) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100dvh' }}>
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }
  if (me.isError || !value) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/anmelden?weiter=${encodeURIComponent(next)}`} replace />;
  }
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <RequireSession>');
  return value;
}

/** Only allow relative in-app paths as redirect target. */
export function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}
